import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TutorialModal from './TutorialModal';
import { TUTORIAL_STEPS } from './TutorialSteps';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  RotateCcw,
  Lightbulb,
  Keyboard
} from 'lucide-react';

const markdownComponents = {
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 text-slate-700 dark:text-slate-200 leading-relaxed">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-4 mb-2 space-y-1 text-slate-700 dark:text-slate-200">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-4 mb-2 space-y-1 text-slate-700 dark:text-slate-200">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-snug">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900 dark:text-white">{children}</strong>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-blue-600 dark:text-blue-400 underline underline-offset-2 hover:text-blue-800 dark:hover:text-blue-300"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  )
};

const Tutorial = ({
  isOpen = false,
  onClose,
  onComplete,
  onSkipTour,
  tutorialStep = 0,
  onStepChange,
  className = ''
}) => {
  const [currentStep, setCurrentStep] = useState(tutorialStep);
  const [isAnimating, setIsAnimating] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(() => new Set());

  const tutorialSteps = TUTORIAL_STEPS;
  const currentStepData = tutorialSteps[currentStep];
  const StepIcon = currentStepData?.icon;

  useEffect(() => {
    if (!isOpen) return;
    setCurrentStep(tutorialStep);
  }, [isOpen, tutorialStep]);

  useEffect(() => {
    if (!isOpen) return;
    const stepId = tutorialSteps[currentStep]?.id;
    if (stepId === 'data-panels' || stepId === 'output-module' || stepId === 'chatbot') {
      window.dispatchEvent(new CustomEvent('tutorial-focus-chat'));
    }
  }, [isOpen, currentStep, tutorialSteps]);

  const goToStep = useCallback(
    (stepIndex) => {
      if (stepIndex >= 0 && stepIndex < tutorialSteps.length) {
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentStep(stepIndex);
          setCompletedSteps((prev) => new Set([...prev, currentStep]));
          onStepChange?.(stepIndex);
          setIsAnimating(false);
        }, 150);
      }
    },
    [tutorialSteps.length, currentStep, onStepChange]
  );

  const nextStep = useCallback(() => goToStep(currentStep + 1), [currentStep, goToStep]);
  const prevStep = useCallback(() => goToStep(currentStep - 1), [currentStep, goToStep]);

  const completeTutorial = useCallback(() => {
    setCompletedSteps((prev) => new Set([...prev, currentStep]));
    onComplete?.();
  }, [currentStep, onComplete]);

  const skipEntireTour = useCallback(() => {
    if (onSkipTour) onSkipTour();
    else onComplete?.();
  }, [onSkipTour, onComplete]);

  const restartTutorial = useCallback(() => {
    setCurrentStep(0);
    setCompletedSteps(new Set());
    onStepChange?.(0);
  }, [onStepChange]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          if (currentStepData.actions.includes('next')) nextStep();
          break;
        case 'ArrowLeft':
          if (currentStepData.actions.includes('prev')) prevStep();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, currentStep, currentStepData, nextStep, prevStep]);

  if (!isOpen || !currentStepData) return null;

  const isCompletionStep = currentStepData.actions?.includes('complete');
  const showSkipTour = typeof onSkipTour === 'function' && !isCompletionStep;
  const tips = currentStepData.tips?.length ? currentStepData.tips : null;

  return (
    <TutorialModal
      isOpen={isOpen}
      onClose={onClose}
      title={currentStepData.title}
      titleIcon={StepIcon || undefined}
      size="lg"
      className={className}
    >
      <div className="flex flex-col h-full max-h-[min(85vh,720px)]">
        <div className="px-4 pb-2 border-b border-slate-200 dark:border-slate-700/80">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Step {currentStep + 1} of {tutorialSteps.length}
            {currentStepData.subtitle ? ` · ${currentStepData.subtitle}` : ''}
          </p>
          {currentStepData.description && (
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{currentStepData.description}</p>
          )}
        </div>

        <div className="px-4 py-2">
          <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-1.5 dark:bg-slate-700" role="progressbar" aria-valuenow={currentStep + 1} aria-valuemin={1} aria-valuemax={tutorialSteps.length}>
            <div
              className="bg-gradient-to-r from-blue-500 to-cyan-500 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${((currentStep + 1) / tutorialSteps.length) * 100}%` }}
            />
          </div>
        </div>

        <div
          className={`px-4 flex-1 min-h-0 flex flex-col transition-opacity duration-150 ${isAnimating ? 'opacity-50' : 'opacity-100'}`}
        >
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {currentStepData.content}
            </ReactMarkdown>

            {tips && (
              <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-600/30">
                <div className="flex items-center gap-2 text-amber-900 dark:text-amber-200 font-medium text-xs mb-2">
                  <Lightbulb className="w-3.5 h-3.5 shrink-0" aria-hidden />
                  Quick tips
                </div>
                <ul className="text-xs text-amber-950/90 dark:text-amber-100/90 space-y-1.5 list-disc pl-4">
                  {tips.map((tip) => (
                    <li key={tip}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 py-2 text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-700/60 mt-2 shrink-0">
            <Keyboard className="w-3.5 h-3.5 shrink-0 opacity-70" aria-hidden />
            <span>
              <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono text-[10px]">←</kbd>{' '}
              <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono text-[10px]">→</kbd> steps ·{' '}
              <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono text-[10px]">Enter</kbd> next ·{' '}
              <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono text-[10px]">Esc</kbd> close
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-t border-slate-200 dark:border-slate-700/80 shrink-0">
          <div className="flex flex-wrap items-center gap-2">
            {currentStepData.actions.includes('prev') && (
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 0}
                className="flex items-center gap-2 px-3 py-2 bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors text-sm dark:disabled:bg-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
            )}

            {currentStepData.actions.includes('restart') && (
              <button
                type="button"
                onClick={restartTutorial}
                className="flex items-center gap-2 px-3 py-2 bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200 rounded-lg transition-colors text-sm"
              >
                <RotateCcw className="w-4 h-4" />
                Restart
              </button>
            )}

            {showSkipTour && (
              <button
                type="button"
                onClick={skipEntireTour}
                className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white underline-offset-2 hover:underline rounded-lg"
              >
                Skip tour
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {currentStepData.actions.includes('next') && (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {currentStepData.actions.includes('complete') && (
              <button
                type="button"
                onClick={completeTutorial}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <CheckCircle className="w-4 h-4" />
                Done
              </button>
            )}
          </div>
        </div>

        <div className="flex justify-center gap-1.5 px-4 pb-3 flex-wrap" role="tablist" aria-label="Tutorial steps">
          {tutorialSteps.map((_, index) => (
            <button
              key={_.id || index}
              type="button"
              onClick={() => goToStep(index)}
              className={`h-2 rounded-full transition-all duration-200 ${
                index === currentStep
                  ? 'bg-blue-500 w-8'
                  : completedSteps.has(index)
                    ? 'bg-emerald-500 w-2'
                    : 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-400 dark:hover:bg-slate-500 w-2'
              }`}
              aria-label={`Go to step ${index + 1}`}
              aria-current={index === currentStep ? 'step' : undefined}
            />
          ))}
        </div>
      </div>
    </TutorialModal>
  );
};

export default Tutorial;
